import React from "react";
import { useNotification } from "@context/NotificationContext";
import { getDeficiencyErrorMessage } from "@features/deficiencies/hooks/useDeficiencyDocumentUpload";
import type {
  DeficiencyCompletePayload,
  DeficiencyDocument,
  DeficiencyResolutionPayload,
} from "@services/deficiencies/types";
import {
  claimDeficiencyById,
  completeDeficiencyById,
  resolveDeficiencyById,
  unclaimDeficiencyById,
} from "@services/deficienciesApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export type DeficiencyDrawerName = "resolveDrawer" | "approveDrawer" | "rejectDrawer";

export interface ResolveDeficiencySubmitValues extends DeficiencyResolutionPayload {
  deficiencyId?: string;
}

export interface ApproveDeficiencySubmitValues {
  deficiencyId?: string;
  comments?: string;
  attachments?: DeficiencyDocument[];
}

export interface RejectDeficiencySubmitValues {
  deficiencyId?: string;
  comments?: string;
  attachments?: DeficiencyDocument[];
}

type UseDeficiencyFlowOptions = {
  onResolveSuccess?: (variables: { id: string; payload: DeficiencyResolutionPayload }) => void;
};

const DEFAULT_OPEN_DRAWERS: Record<DeficiencyDrawerName, boolean> = {
  resolveDrawer: false,
  approveDrawer: false,
  rejectDrawer: false,
};

export const useDeficiencyFlow = (options?: UseDeficiencyFlowOptions) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const [openDrawers, setOpenDrawers] =
    React.useState<Record<DeficiencyDrawerName, boolean>>(DEFAULT_OPEN_DRAWERS);
  const [selectedDeficiencyId, setSelectedDeficiencyId] = React.useState<string | undefined>();

  const closeDrawer = React.useCallback((drawerName: DeficiencyDrawerName) => {
    setOpenDrawers((prev) => ({
      ...prev,
      [drawerName]: false,
    }));

    if (drawerName === "resolveDrawer") {
      setSelectedDeficiencyId(undefined);
    }
  }, []);

  const invalidateDeficiencyQueries = React.useCallback(
    async (deficiencyId: string) => {
      await queryClient.invalidateQueries({ queryKey: ["deficiencies"] });
      await queryClient.invalidateQueries({ queryKey: ["Deficiency", deficiencyId] });
    },
    [queryClient],
  );

  const notifyMutationError = React.useCallback(
    (error: unknown) => {
      notify({
        title: t("common.error"),
        description: getDeficiencyErrorMessage(error) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    [notify, t],
  );

  const openDrawer = React.useCallback(
    (drawerName: DeficiencyDrawerName, deficiencyId?: string) => {
      if (drawerName === "resolveDrawer") {
        setSelectedDeficiencyId(deficiencyId);
      }

      setOpenDrawers((prev) => ({
        ...prev,
        [drawerName]: true,
      }));
    },
    [],
  );

  const resolveDeficiencyMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DeficiencyResolutionPayload }) =>
      resolveDeficiencyById({ id, payload }),
    onSuccess: async (_, variables) => {
      closeDrawer("resolveDrawer");
      options?.onResolveSuccess?.(variables);

      notify({
        title: t("deficiencies.notifications.resolve-success-title"),
        description: t("deficiencies.notifications.resolve-success-description"),
        type: "success",
      });

      await queryClient.invalidateQueries({ queryKey: ["deficiencies"] });

      if (!options?.onResolveSuccess) {
        await queryClient.invalidateQueries({ queryKey: ["Deficiency", variables.id] });
      }
    },
    onError: (error) => {
      notify({
        title: t("deficiencies.notifications.resolve-error-title"),
        description:
          getDeficiencyErrorMessage(error) ??
          t("deficiencies.notifications.resolve-error-description"),
        type: "error",
      });
    },
  });

  const claimDeficiencyMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => claimDeficiencyById({ id }),
    onSuccess: async (_, variables) => {
      await invalidateDeficiencyQueries(variables.id);
      notify({
        title: t("deficiencies.notifications.deficiency-claimed"),
        type: "success",
      });
    },
    onError: notifyMutationError,
  });

  const unclaimDeficiencyMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => unclaimDeficiencyById({ id }),
    onSuccess: async (_, variables) => {
      await invalidateDeficiencyQueries(variables.id);
      notify({
        title: t("deficiencies.notifications.deficiency-unclaimed"),
        type: "success",
      });
    },
    onError: notifyMutationError,
  });

  const completeDeficiencyMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DeficiencyCompletePayload }) =>
      completeDeficiencyById({ id, payload }),
    onSuccess: async (_, variables) => {
      const isApproveAction = variables.payload.status === "APPROVED";

      closeDrawer(isApproveAction ? "approveDrawer" : "rejectDrawer");

      notify({
        title: isApproveAction
          ? t("deficiencies.notifications.approve-success-title")
          : t("deficiencies.notifications.reject-success-title"),
        type: "success",
      });

      await invalidateDeficiencyQueries(variables.id);
    },
    onError: (error, variables) => {
      const isApproveAction = variables.payload.status === "APPROVED";

      closeDrawer(isApproveAction ? "approveDrawer" : "rejectDrawer");
      notifyMutationError(error);
    },
  });

  const handleResolveSubmit = React.useCallback(
    ({ deficiencyId, ...payload }: ResolveDeficiencySubmitValues) => {
      const resolvedDeficiencyId = deficiencyId ?? selectedDeficiencyId;

      if (!resolvedDeficiencyId) {
        notify({
          title: t("deficiencies.notifications.resolve-error-title"),
          description: t("common.something-went-wrong"),
          type: "error",
        });
        return;
      }

      resolveDeficiencyMutation.mutate({
        id: resolvedDeficiencyId,
        payload,
      });
    },
    [notify, resolveDeficiencyMutation, selectedDeficiencyId, t],
  );

  const handleClaimDeficiency = React.useCallback(
    async (deficiencyId?: string) => {
      if (!deficiencyId) {
        notify({
          title: t("common.error"),
          description: t("common.something-went-wrong"),
          type: "error",
        });
        throw new Error(t("common.something-went-wrong"));
      }

      await claimDeficiencyMutation.mutateAsync({ id: deficiencyId });
    },
    [claimDeficiencyMutation, notify, t],
  );

  const handleUnclaimDeficiency = React.useCallback(
    async (deficiencyId?: string) => {
      if (!deficiencyId) {
        notify({
          title: t("common.error"),
          description: t("common.something-went-wrong"),
          type: "error",
        });
        throw new Error(t("common.something-went-wrong"));
      }

      await unclaimDeficiencyMutation.mutateAsync({ id: deficiencyId });
    },
    [notify, t, unclaimDeficiencyMutation],
  );

  const handleApproveSubmit = React.useCallback(
    async ({ deficiencyId, comments, attachments }: ApproveDeficiencySubmitValues) => {
      if (!deficiencyId) {
        notify({
          title: t("common.error"),
          description: t("common.something-went-wrong"),
          type: "error",
        });
        throw new Error(t("common.something-went-wrong"));
      }

      await completeDeficiencyMutation.mutateAsync({
        id: deficiencyId,
        payload: {
          status: "APPROVED",
          approvalNotes: comments,
          approvalDocuments: attachments,
        },
      });
    },
    [completeDeficiencyMutation, notify, t],
  );
  const handleRejectSubmit = React.useCallback(
    async ({ deficiencyId, comments, attachments }: RejectDeficiencySubmitValues) => {
      if (!deficiencyId) {
        notify({
          title: t("common.error"),
          description: t("common.something-went-wrong"),
          type: "error",
        });
        throw new Error(t("common.something-went-wrong"));
      }

      await completeDeficiencyMutation.mutateAsync({
        id: deficiencyId,
        payload: {
          status: "REJECTED",
          rejectionNotes: comments,
          rejectionDocuments: attachments,
        },
      });
    },
    [completeDeficiencyMutation, notify, t],
  );

  return {
    openDrawers,
    selectedDeficiencyId,
    openDrawer,
    closeDrawer,
    handleResolveSubmit,
    handleClaimDeficiency,
    handleUnclaimDeficiency,
    handleApproveSubmit,
    handleRejectSubmit,
    isResolvingDeficiency: resolveDeficiencyMutation.isPending,
    isClaimingDeficiency: claimDeficiencyMutation.isPending,
    isUnclaimingDeficiency: unclaimDeficiencyMutation.isPending,
    isCompletingDeficiency: completeDeficiencyMutation.isPending,
  };
};
